import api, { unwrapList, unwrapOne } from "./apiClient";
import { BACKEND_URL } from "@/config/api";
import orderService from "./orderService";

const API_BASE = `${BACKEND_URL}/stocks`;

// Disponível igual ou abaixo disso = "Baixo", quando o lote não tem mínimo
export const DEFAULT_LOW_STOCK = 10;
// Pedidos já encerrados não aparecem como reserva do lote
const CLOSED_ORDER_STATUSES = ["Finalizado", "Cancelado"];
// Validade: até 7 dias é crítico; até 30 dias entra em "A vencer"
export const EXPIRING_CRITICAL_DAYS = 7;
export const EXPIRING_DAYS = 30;

/**
 * Stock Service
 * Gerencia todas as operações de estoque
 */
class StockService {
  /**
   * Get all stocks
   * @param {object} [options] - config extra do axios (ex.: { silent: true })
   * @returns {Promise<Array>}
   */
  async getAllStocks(options = {}) {
    try {
      const response = await api.get(API_BASE, options);
      return unwrapList(response);
    } catch (error) {
      if (error.response?.status === 404) {
        return [];
      }
      console.error("❌ Erro ao buscar estoque:", error);
      throw error;
    }
  }

  /**
   * Get stock by ID
   * @param {number} id - Stock ID
   * @returns {Promise<object>}
   */
  async getStockById(id) {
    try {
      const response = await api.get(
        `${API_BASE}/${id}`
      );
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar estoque ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new stock entry
   * @param {object} stockData - Stock data
   * @returns {Promise<object>}
   */
  async createStock(stockData) {
    try {
      // Validar dados antes de enviar
      this.validateStockData(stockData);

      const response = await api.post(
        API_BASE,
        stockData
      );
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao criar entrada de estoque:", error);
      throw error;
    }
  }

  /**
   * Update stock entry
   * @param {number} id - Stock ID
   * @param {object} stockData - Updated stock data
   * @returns {Promise<object>}
   */
  async updateStock(id, stockData) {
    try {
      // Validar dados antes de enviar (não exigir product_id na atualização)
      this.validateStockData(stockData, true);

      const response = await api.put(
        `${API_BASE}/${id}`,
        stockData
      );
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao atualizar estoque ${id}:`, error);
      throw error;
    }
  }

  /**
   * Corrige reservas órfãs do produto (POST /admin/stock/adjust/{productCode})
   * @param {string} productCode - code UUID do produto
   * @returns {Promise<object>} { success, message, ... } (JSON direto)
   */
  async adjustReservations(productCode) {
    try {
      const response = await api.post(
        `${BACKEND_URL}/admin/stock/adjust/${productCode}`
      );
      return response.data;
    } catch (error) {
      console.error(`❌ Erro ao ajustar estoque do produto ${productCode}:`, error);
      throw error;
    }
  }

  /**
   * Movimenta o estoque físico do lote (entrada > 0, saída < 0).
   * A API não tem endpoint de movimentação: relê o lote para partir da
   * quantidade atual (pedidos podem ter reservado/baixado nesse meio tempo)
   * e envia só `current_quantity` no PUT (atualização parcial).
   * @param {number|string} id - Stock ID
   * @param {number} delta - unidades a somar (negativo = saída)
   * @returns {Promise<object>} lote atualizado
   */
  async moveStock(id, delta) {
    try {
      if (!Number.isInteger(Number(delta)) || Number(delta) === 0) {
        throw new Error("Informe uma quantidade inteira maior que zero");
      }

      const fresh = await this.getStockById(id);
      const current = Number(fresh?.current_quantity || 0);
      const reserved = Number(fresh?.reserved_quantity || 0);
      const next = current + Number(delta);
      if (next < reserved) {
        throw new Error(
          `Saída maior que o disponível: há ${current - reserved} un. livres (${reserved} reservada${reserved === 1 ? "" : "s"} para pedidos)`
        );
      }

      const response = await api.put(`${API_BASE}/${id}`, {
        current_quantity: next,
      });
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao movimentar estoque ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete stock entry
   * @param {number} id - Stock ID
   * @returns {Promise<void>}
   */
  async deleteStock(id) {
    try {
      await api.delete(`${API_BASE}/${id}`);
    } catch (error) {
      console.error(`❌ Erro ao deletar estoque ${id}:`, error);
      throw error;
    }
  }

  /**
   * Validate stock data
   * @param {object} data - Stock data to validate
   * @param {boolean} isUpdate - If true, product_id is optional
   * @throws {Error} If validation fails
   */
  validateStockData(data, isUpdate = false) {
    const errors = [];

    // product_id é obrigatório apenas na criação
    if (!isUpdate && !data.product_id) {
      errors.push("Produto é obrigatório");
    }

    if (!data.storage_location_id) {
      errors.push("Local de armazenamento é obrigatório");
    }

    if (!data.lot_code || data.lot_code.trim() === "") {
      errors.push("Código do lote é obrigatório");
    }

    if (
      data.current_quantity === null ||
      data.current_quantity === undefined ||
      data.current_quantity < 0
    ) {
      errors.push("Quantidade deve ser maior ou igual a zero");
    }

    if (
      data.unit_cost === null ||
      data.unit_cost === undefined ||
      data.unit_cost <= 0
    ) {
      errors.push("Custo unitário deve ser maior que zero");
    }

    if (!data.manufacture_date) {
      errors.push("Data de fabricação é obrigatória");
    }

    if (!data.expiration_date) {
      errors.push("Data de validade é obrigatória");
    }

    // Validar se data de validade é posterior à data de fabricação
    if (data.manufacture_date && data.expiration_date) {
      const manufactureDate = this.parseDate(data.manufacture_date);
      const expirationDate = this.parseDate(data.expiration_date);

      if (expirationDate <= manufactureDate) {
        errors.push("Data de validade deve ser posterior à data de fabricação");
      }
    }

    if (errors.length > 0) {
      throw new Error(errors.join(", "));
    }
  }

  /**
   * Converte data da API em Date local. "yyyy-MM-dd" (LocalDate) é lido
   * como dia local: `new Date("2026-10-24")` seria meia-noite UTC e, no
   * fuso do Brasil, viraria 23/10.
   * @param {string} date - "yyyy-MM-dd" ou ISO com hora
   * @returns {Date|null}
   */
  parseDate(date) {
    if (!date) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(date).trim());
    const parsed = match
      ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      : new Date(date);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  /**
   * Pedidos em que o lote está reservado (`active_reservations`). A API só
   * devolve reservas RESERVED: confirmada a reserva, o produto já saiu do
   * estoque e o pedido deixa de aparecer aqui.
   * @param {object} stock - lote
   * @returns {Array<object>} { order_code, order_status, order_date, reserved_quantity, ... }
   */
  getActiveReservations(stock) {
    const reservations = Array.isArray(stock?.active_reservations)
      ? stock.active_reservations
      : [];
    return reservations.filter(
      (r) => !r.status || String(r.status).toUpperCase() === "RESERVED"
    );
  }

  /**
   * Pedido da reserva já encerrado (finalizado ou cancelado)?
   * @param {object} reservation - item de `active_reservations`
   * @returns {boolean}
   */
  isClosedOrderReservation(reservation) {
    return CLOSED_ORDER_STATUSES.includes(
      orderService.getStatusName(reservation?.order_status)
    );
  }

  /**
   * Calculate available quantity (current - reserved)
   * @param {number} currentQuantity - Current quantity
   * @param {number} reservedQuantity - Reserved quantity
   * @returns {number}
   */
  calculateAvailableQuantity(currentQuantity, reservedQuantity) {
    return (currentQuantity || 0) - (reservedQuantity || 0);
  }

  /**
   * Calculate days until expiration
   * @param {string} expirationDate - Expiration date (ISO format)
   * @returns {number}
   */
  calculateDaysUntilExpiration(expirationDate) {
    const expDate = this.parseDate(expirationDate);
    if (!expDate) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expDate.setHours(0, 0, 0, 0);

    // round: o horário de verão já deixou dias com 23/25 h
    return Math.round((expDate - today) / (1000 * 60 * 60 * 24));
  }

  /**
   * Get expiration status
   * @param {string} expirationDate - Expiration date (ISO format)
   * @returns {object} Status with color and label
   */
  getExpirationStatus(expirationDate) {
    const days = this.calculateDaysUntilExpiration(expirationDate);

    if (days === null) {
      return { color: "gray", label: "Sem data", variant: "secondary" };
    }

    if (days < 0) {
      return { color: "red", label: "Vencido", variant: "destructive" };
    }

    if (days === 0) {
      return { color: "red", label: "Vence hoje", variant: "destructive" };
    }

    if (days <= EXPIRING_CRITICAL_DAYS) {
      return {
        color: "red",
        label: `${days}d restante${days > 1 ? "s" : ""}`,
        variant: "destructive",
      };
    }

    if (days <= EXPIRING_DAYS) {
      return {
        color: "yellow",
        label: `${days}d restante${days > 1 ? "s" : ""}`,
        variant: "warning",
      };
    }

    return {
      color: "green",
      label: `${days}d restante${days > 1 ? "s" : ""}`,
      variant: "default",
    };
  }

  /**
   * Get stock status based on quantity
   * @param {number} currentQuantity - Current quantity
   * @param {number} reservedQuantity - Reserved quantity
   * @param {number} [minQuantity] - mínimo do lote (`min_quantity`); sem
   *   mínimo cadastrado usa DEFAULT_LOW_STOCK
   * @returns {object} Status with color and label
   */
  getStockStatus(currentQuantity, reservedQuantity, minQuantity) {
    const available = this.calculateAvailableQuantity(
      currentQuantity,
      reservedQuantity
    );
    const threshold =
      Number(minQuantity) > 0 ? Number(minQuantity) : DEFAULT_LOW_STOCK;

    if (available <= 0) {
      return { color: "red", label: "Esgotado", variant: "destructive" };
    }

    if (available <= threshold) {
      return { color: "yellow", label: "Baixo", variant: "warning" };
    }

    return { color: "green", label: "Disponível", variant: "success" };
  }

  /**
   * Format currency to BRL
   * @param {number} value - Value to format
   * @returns {string}
   */
  formatCurrency(value) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  }

  /**
   * Format date to Brazilian format
   * @param {string} date - ISO date string
   * @returns {string}
   */
  formatDate(date) {
    const parsed = this.parseDate(date);
    return parsed ? parsed.toLocaleDateString("pt-BR") : "-";
  }

  /**
   * Calculate total stock value
   * @param {number} quantity - Quantity
   * @param {number} unitCost - Unit cost
   * @returns {number}
   */
  calculateTotalValue(quantity, unitCost) {
    return (quantity || 0) * (unitCost || 0);
  }
}

export default new StockService();
